import { __awaiter } from 'tslib';
import { bufferToBase64 } from '../../utils/convertors.js';
import { ProviderLookup } from '../ProviderLookup.js';
import { normalizeWalletName } from '../../utils/normalizeWalletName/normalizeWalletName.js';

class SolProviderHelper {
    constructor() {
        this.GlowProvider = () => this.installedProviderLookup([{ flag: 'isGlow', value: true }]);
        this.CoinbaseProvider = () => this.findSolanaProviders(window.coinbaseSolana)[0];
        this.PhantomProvider = () => this.installedProviderLookup([
            { flag: 'isPhantom', value: true },
            { flag: 'isBraveWallet', value: false },
            { flag: 'isExodus', value: false },
        ]);
        this.SolflareProvider = () => this.installedProviderLookup([{ flag: 'isSolflare', value: true }]);
        this.BraveProvider = () => this.installedProviderLookup([
            { flag: 'isPhantom', value: true },
            { flag: 'isBraveWallet', value: true },
        ]);
        this.ExodusProvider = () => this.installedProviderLookup([
            { flag: 'isPhantom', value: true },
            { flag: 'isExodus', value: true },
        ]);
        this.providers = {
            bravesol: this.BraveProvider,
            coinbasesolana: this.CoinbaseProvider,
            exodussol: this.ExodusProvider,
            glow: this.GlowProvider,
            phantom: this.PhantomProvider,
            phantomledger: this.PhantomProvider,
            solflare: this.SolflareProvider,
        };
    }
    installedProviderLookup(providerFlags) {
        return ProviderLookup(this.allInstalledProviders(), providerFlags);
    }
    allInstalledProviders() {
        return this.findSolanaProviders(window.solana).concat(this.findSolanaProviders(window.solflare), this.findSolanaProviders(window.glowSolana));
    }
    findSolanaProviders(solana) {
        const solanaProviders = [];
        if (solana && !solana.providers) {
            solanaProviders.push(solana);
        }
        else if (solana && solana.providers) {
            solana.providers.forEach((provider) => {
                solanaProviders.push(provider);
            });
        }
        return solanaProviders;
    }
    isInstalledHelper(walletName) {
        return this.findProvider(walletName) !== undefined;
    }
    findProvider(walletName) {
        var _a, _b;
        return (_b = (_a = this.providers)[normalizeWalletName(walletName)]) === null || _b === void 0 ? void 0 : _b.call(_a);
    }
    fetchPublicAddressWithName(name) {
        return __awaiter(this, void 0, void 0, function* () {
            const provider = this.findProvider(name);
            if (!provider) {
                return Promise.resolve(undefined);
            }
            yield provider.connect();
            const { publicKey } = provider;
            return publicKey ? publicKey.toString() : Promise.resolve(undefined);
        });
    }
    connectWithName(name) {
        return __awaiter(this, void 0, void 0, function* () {
            const provider = this.findProvider(name);
            if (!provider) {
                return Promise.resolve(undefined);
            }
            try {
                yield provider.connect();
                return provider;
            }
            catch (err) {
                return undefined;
            }
        });
    }
    signMessageWithName(messageToSign, name) {
        return __awaiter(this, void 0, void 0, function* () {
            const walletAddress = yield this.fetchPublicAddressWithName(name);
            if (!walletAddress) {
                return Promise.resolve(undefined);
            }
            const provider = this.findProvider(name);
            if (!provider) {
                return Promise.resolve(undefined);
            }
            const encodedMessage = new TextEncoder().encode(messageToSign);
            return provider.connect().then(() => __awaiter(this, void 0, void 0, function* () {
                const { signature } = (yield provider.signMessage(encodedMessage, 'utf8'));
                return bufferToBase64(signature);
            }));
        });
    }
    _setupEventListeners(listeners, web3Provider) {
        const handleAccountChange = (address) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            if (!address) {
                yield (web3Provider === null || web3Provider === void 0 ? void 0 : web3Provider.connect());
                if ((_a = web3Provider === null || web3Provider === void 0 ? void 0 : web3Provider.publicKey) === null || _a === void 0 ? void 0 : _a.toString()) {
                    (_b = listeners.onAccountChange) === null || _b === void 0 ? void 0 : _b.call(listeners, [web3Provider.publicKey.toString()]);
                }
                return;
            }
            if (address.toString()) {
                yield ((_c = listeners.onAccountChange) === null || _c === void 0 ? void 0 : _c.call(listeners, [address.toString()]));
            }
        });
        const handleDisconnect = () => __awaiter(this, void 0, void 0, function* () {
            var _d;
            yield ((_d = listeners.onDisconnect) === null || _d === void 0 ? void 0 : _d.call(listeners));
        });
        web3Provider === null || web3Provider === void 0 ? void 0 : web3Provider.on('accountChanged', handleAccountChange);
        web3Provider === null || web3Provider === void 0 ? void 0 : web3Provider.on('disconnect', handleDisconnect);
        return () => { };
    }
    _teardownEventListeners(name) {
        const web3Provider = this.findProvider(name);
        if (!web3Provider) {
            return;
        }
        web3Provider.removeAllListeners();
    }
    getConnectedAccountsWithName(name) {
        return __awaiter(this, void 0, void 0, function* () {
            const provider = this.findProvider(name);
            if (!provider || !provider.publicKey) {
                return [];
            }
            return [provider.publicKey.toString()];
        });
    }
}

export { SolProviderHelper };
